import { useCallback } from 'react';
import styled from 'styled-components';

import Card from 'components/insights/layout/Card';
import AnalyticsResponseData from 'components/insights/query/AnalyticsResponseData';
import { ScrollContainer } from 'components/shared/DataTable';
import {
  useInboundReferralsInsightsActions,
  useInboundReferralsInsightsStore,
} from 'stores/inboundReferralsInsightsStore';

import { INBOUND_VIEW_BY_DIMENSION_MAP, INBOUND_VIEW_BY_ENTITY_HEADER } from '../constants';

import { EntityButton, InboundEntityCellRenderer } from './inboundReferralDetailsColumns';
import InboundReferralDetailsHeader from './InboundReferralDetailsHeader';
import InboundReferralDetailsTable from './InboundReferralDetailsTable';

type Props = {
  loading: boolean;
  data: AnalyticsResponseData;
};

export default function InboundReferralDetailsSection({ loading, data }: Props) {
  const viewBy = useInboundReferralsInsightsStore((state) => state.viewBy);
  const { openSlideout } = useInboundReferralsInsightsActions();
  const entityHeader = INBOUND_VIEW_BY_ENTITY_HEADER[viewBy];
  const dimension = INBOUND_VIEW_BY_DIMENSION_MAP[viewBy];

  const entityCell = useCallback<InboundEntityCellRenderer>(
    (info) => {
      const { entityId, entityName } = info.row.original;
      // A "None" row (no facility name / null referral or payer source) still drills — the slideout
      // filter maps an empty entityId to the IS-NULL sentinel (getInboundSlideoutEntityFilter).
      return (
        <EntityButton type='button' onClick={() => openSlideout({ entityId, entityName, dimension })}>
          {info.getValue()}
        </EntityButton>
      );
    },
    [dimension, openSlideout]
  );

  return (
    <SectionCard>
      <InboundReferralDetailsHeader />
      <TableWrapper>
        <InboundReferralDetailsTable
          loading={loading}
          data={data}
          entityHeader={entityHeader}
          dimension={dimension}
          entityCell={entityCell}
        />
      </TableWrapper>
    </SectionCard>
  );
}

const SectionCard = styled(Card)`
  padding: 0;
  display: flex;
  flex-direction: column;
  border-radius: 4px;
`;

const TableWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding: 0 24px 24px;

  td:first-child:not(:last-child),
  th:first-child {
    border-right: 1px solid ${({ theme }) => theme.colors.black10};
  }

  /* Stretch the divider cell to the full row height so its border spans multi-line rows
     (e.g. a wrapped entity name) instead of stopping at its own content height. */
  td:first-child {
    align-self: stretch;
  }

  @media (max-width: 800px) {
    padding: 0;

    ${ScrollContainer} {
      border-top-left-radius: 0;
      border-top-right-radius: 0;
      border-left: none;
      border-right: none;
      border-bottom: none;
    }
  }
`;
